// Fallbacks used only when the clinic profile has not been filled in yet.
export const CLINIC_DEFAULTS = {
  clinicName: 'Aditya Homeopathy Clinic',
  doctorName: 'Dr. Sheetal Khodke',
  qualification: 'M.D Homeopathic',
  mobile: '+91 9920796527',
  ext: '123',
  timings: 'Monday To Saturday (Morning 9AM to Evening 6PM)',
  address:
    'Shop No.12, Opp Y C School, Sector 14, Koparkhairne, Navi Mumbai, Maharashtra 400709',
};

export const formatDate = (value) => {
  if (!value) return '____________';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB');
};

// yyyy-mm-dd for <input type="date" />
export const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

// Every template declares which extra fields it needs, so the editor form and
// the printed document stay in sync when a template is added or changed.
export const certificateTemplates = {
  medicalLeave: {
    title: 'Medical Leave Certificate',
    label: 'Medical Certificate',
    description: 'Certifies bed rest / unfitness for a date range.',
    fields: ['fromDate', 'toDate', 'place'],
    body: (d) => `
This is to certify that Mr./Ms. ${d.patientName} was examined and treated
at ${d.clinicName} under my medical supervision.

Based on medical evaluation, the patient was advised complete
bed rest from ${formatDate(d.fromDate)} to ${formatDate(d.toDate)}.

During this period, the patient was not medically fit to attend
office, school, or perform routine activities.

This certificate is issued on medical grounds upon request.
`,
  },

  fitness: {
    title: 'Fitness Certificate',
    label: 'Fitness Certificate',
    description: 'Certifies the patient is fit to resume normal duties.',
    fields: ['fromDate', 'toDate', 'place'],
    body: (d) => `
This is to certify that Mr./Ms. ${d.patientName} was under my treatment
at ${d.clinicName}.

Upon examination, the patient has recovered satisfactorily
and is medically fit to resume normal duties.

The patient may return to work with effect from ${formatDate(d.toDate || d.fromDate)}.
`,
  },

  medicineTravel: {
    title: 'Travel Medication Certificate',
    label: 'Travel Certificate',
    description: 'Certifies prescribed medicines carried while travelling.',
    fields: ['fromDate', 'place'],
    body: (d) => `
This is to certify that Mr./Ms. ${d.patientName} is under my medical care
and requires prescribed medicines during travel.

The medicines are for personal therapeutic use only.

This certificate is issued on ${formatDate(d.fromDate)} for official purposes.
`,
  },

  invoice: {
    title: 'Medical Invoice',
    label: 'Invoice',
    description: 'Consultation fee receipt for reimbursement.',
    fields: ['fromDate', 'fees', 'place'],
    body: (d) => `
Patient Name: ${d.patientName}
Invoice Date: ${formatDate(d.fromDate)}

Consultation and medical services were provided at
${d.clinicName}.

Total Consultation Fees: ${d.fees} (Paid)

This invoice is issued for medical and reimbursement purposes.
`,
  },

  prescription: {
    title: 'Prescription',
    label: 'Prescription',
    description: 'Printed prescription slip on the clinic letterhead.',
    fields: ['fromDate'],
    body: (d) => `
Patient Name: ${d.patientName}
Date: ${formatDate(d.fromDate)}

The following medicines are prescribed after clinical evaluation.
Please follow the dosage strictly as advised.

Complete the full course and avoid self-medication.
`,
  },

  custom: {
    title: 'Medical Certificate',
    label: 'Custom Certificate',
    description: 'Blank letterhead — write your own title and text.',
    fields: ['fromDate', 'toDate', 'place', 'fees'],
    body: (d) => `
This is to certify that Mr./Ms. ${d.patientName} was examined at
${d.clinicName} on ${formatDate(d.fromDate)}.

`,
  },
};

export const templateKeys = Object.keys(certificateTemplates);

// Labels for the editable inputs, keyed the same way as template.fields
export const FIELD_LABELS = {
  fromDate: 'From / Issue Date',
  toDate: 'To Date',
  place: 'Place',
  fees: 'Consultation Fees',
};

export const DATE_FIELDS = ['fromDate', 'toDate', 'footerDate'];

// Builds the editable form state for a patient + template pair.
export const buildCertificateForm = (customer, type, clinic = CLINIC_DEFAULTS) => {
  const template = certificateTemplates[type];
  const today = new Date().toISOString().slice(0, 10);

  const base = {
    ...CLINIC_DEFAULTS,
    ...clinic,
    type,
    title: template.title,
    patientId: customer._id,
    patientName: `${customer.firstName || ''} ${customer.surname || ''}`.trim(),
    patientMobile: customer.mobile || '',
    fromDate: today,
    toDate: '',
    footerDate: today,
    fees: '',
    disease: '',
    duration: '',
    place: '',
  };

  return { ...base, bodyText: template.body(base).trim() };
};

// Maps a saved certificate document back into editable form state.
export const certificateToForm = (certificate) => ({
  ...CLINIC_DEFAULTS,
  ...certificate,
  patientId: certificate.patientId,
  fromDate: toDateInput(certificate.fromDate),
  toDate: toDateInput(certificate.toDate),
  footerDate: toDateInput(certificate.footerDate),
  fees: certificate.fees || '',
  disease: certificate.disease || '',
  duration: certificate.duration || '',
  place: certificate.place || '',
  bodyText: certificate.bodyText || '',
});
